import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DriveFileMoveOutlinedIcon from '@mui/icons-material/DriveFileMoveOutlined';
import { Button, CardActions, Chip, Paper } from '@mui/material';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import { DataGrid, GridActionsCellItem, gridClasses } from '@mui/x-data-grid';
import ProductGroupSelectDialog from 'components/ProductGroupSelectDialog';
import { currencyFormatter } from 'core/utils/commonUtils';
import { getMeasureName, getProductTypeName } from 'core/utils/productUtils';
import useProducts, { SWR_PRODUCTS_ENDPOINT } from 'hooks/swr/useProducts';
import useResponsive from 'hooks/useResponsive';
import SimplePage from 'layout/pages/SimplePage/SimplePage';
import { isArray, isNil } from 'lodash';
import { useConfirm } from 'material-ui-confirm';
import { observer } from 'mobx-react-lite';
import { useCallback, useContext, useEffect, useId, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import * as productsService from 'services/productService';
import StoreContext from 'stores/context/StoreContext';
import { mutate } from 'swr';
import ProductDialog from './ProductEdit/ProductDialog';
import ProductGroups from './ProductGroups';
import ProductsPageHeader from './ProductsPageHeader';
import type {
  GridColDef,
  GridRowParams,
  GridRowId,
  GridCallbackDetails,
  GridRowSelectionModel,
} from '@mui/x-data-grid';
import type { ProductGroupSelectedNode } from 'components/ProductGroupSelect/ProductGroupSelect';
import type { Product } from 'models/product/product';
import type { FC } from 'react';

const ProductsPage: FC = observer(() => {
  const componentId = useId();
  const [searchParams, setSearchParams] = useSearchParams();
  const [paginationModel, setPaginationModel] = useState({
    page: +(searchParams.get('pageIndex') ?? 0),
    pageSize: +(searchParams.get('pageSize') ?? 50),
  });
  const [searchText, setSearchText] = useState(searchParams.get('searchText') ?? '');
  const [openDetail, setOpenDetail] = useState(false);
  const [openMoveSelectedDialog, setOpenMoveSelectedDialog] = useState(false);
  const [openMoveIndividualDialog, setOpenMoveIndividualDialog] = useState(false);
  const [editedProductId, setEditedProductId] = useState<number | undefined>();
  const [actionProductId, setActionProductId] = useState<number | undefined>();
  const [isDeleting, setIsDeleting] = useState<Set<GridRowId>>(new Set());
  const [isMoving, setIsMoving] = useState<Set<GridRowId>>(new Set());
  const isSmallScreen = useResponsive('down', 'sm');
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);
  const [selectedGroup, setSelectedGroup] = useState<
    ProductGroupSelectedNode | undefined
  >();

  const isLtMdScreen = useResponsive('down', 'md');
  const isLtSmScreen = useResponsive('down', 'sm');

  const { notifyStore } = useContext(StoreContext);

  const columnVisibilityModel = {
    actions: !isSmallScreen,
  };

  const deletionConfirm = useConfirm();

  const deleteProduct = useCallback(
    (productIds?: number[]): void => {
      if (!isNil(productIds)) {
        deletionConfirm({
          description: `Удалить ${productIds.length > 1 ? 'товары' : 'товар'}?`,
        })
          .then(async () => {
            productIds?.forEach((productId) => {
              setIsDeleting(new Set(isDeleting.add(productId)));
            });

            await productsService.deleteProductAsync(productIds);

            return productIds;
          })
          .then(async () => {
            productIds?.forEach(isDeleting.delete, isDeleting);
            setIsDeleting(new Set(isDeleting));

            changeRowSelection(
              rowSelectionModel.filter((d) => !productIds?.includes(d as number))
            );

            return await resetProductsData();
          })
          .catch((err) => {
            productIds?.forEach(isDeleting.delete, isDeleting);
            setIsDeleting(new Set(isDeleting));

            console.log(err);
          });
      }
    },
    [deletionConfirm, isDeleting, rowSelectionModel]
  );

  const rowClickHandle = (params: GridRowParams): void => {
    setEditedProductId(params.row.id);
    setOpenDetail(true);
  };

  const handleDetailClose = (): void => {
    setOpenDetail(false);
  };

  const { data, isLoading } = useProducts({
    pageIndex: paginationModel.page,
    pageSize: paginationModel.pageSize,
    filterText: searchText,
    groupId: selectedGroup?.id,
  });

  useEffect(() => {
    setSearchParams({
      pageIndex: String(paginationModel.page),
      pageSize: String(paginationModel.pageSize),
      searchText,
    });
  }, [setSearchParams, paginationModel, searchText]);

  const resetProductsData = async (): Promise<unknown> =>
    await mutate((key) => isArray(key) && key[0] === SWR_PRODUCTS_ENDPOINT, undefined);

  const handleDeleteProductClick = useCallback(
    (id: GridRowId) => () => {
      setTimeout(() => {
        deleteProduct([+id]);
      });
    },
    [deleteProduct]
  );

  const columns: Array<GridColDef<Product>> = [
    {
      field: 'name',
      headerName: 'Название',
      display: 'flex',
      sortable: false,
      flex: 1,
      renderCell: (params) => (
        <Stack direction={'row'} gap={0.7} flexWrap={'wrap'} alignItems={'center'}>
          <div>{params.value}</div>
          {!isLtSmScreen &&
            params.row.tags?.map((t) => (
              <Chip
                key={`${componentId}-tag-${t.id}`}
                variant='outlined'
                color='secondary'
                size='small'
                label={t.name}
              />
            ))}
        </Stack>
      ),
    },
    {
      field: 'price',
      headerName: 'Цена',
      minWidth: 150,
      display: 'flex',
      sortable: false,
      renderCell: (params) =>
        `${currencyFormatter.format(params.row.price)}/${getMeasureName(
          params.row.measure,
          true
        )}`,
    },
    {
      field: 'type',
      headerName: 'Тип',
      display: 'flex',
      sortable: false,
      renderCell: (params) => `${getProductTypeName(params.row.type)}`,
    },
    {
      field: 'actions',
      sortable: false,
      type: 'actions',
      width: 40,
      minWidth: 40,
      getActions: (params) => [
        <GridActionsCellItem
          showInMenu
          key={params.row.id}
          disabled={isMoving.has(params.row.id)}
          icon={
            isMoving.has(params.row.id) ? (
              <CircularProgress size={20} />
            ) : (
              <DriveFileMoveOutlinedIcon />
            )
          }
          onClick={() => {
            handleMoveIndividualProductClick(params.row.id);
          }}
          label='Переместить в группу'
        />,
        <GridActionsCellItem
          showInMenu
          key={params.row.id}
          disabled={isDeleting.has(params.row.id)}
          icon={
            isDeleting.has(params.row.id) ? (
              <CircularProgress size={20} />
            ) : (
              <DeleteOutlineIcon />
            )
          }
          onClick={handleDeleteProductClick(params.row.id)}
          label='Удалить'
        />,
      ],
    },
  ];

  const onCreateClickHandle: VoidFunction = () => {
    setEditedProductId(undefined);
    setOpenDetail(true);
  };

  const selectionModelChangeHandler = (
    newRowSelectionModel: GridRowSelectionModel,
    _: GridCallbackDetails
  ): void => {
    changeRowSelection(newRowSelectionModel);
  };

  const changeRowSelection = (newRowSelectionModel: GridRowSelectionModel): void => {
    setRowSelectionModel(newRowSelectionModel);
  };

  const handleMoveSelectedDialogClose = async (groupId?: number): Promise<void> => {
    try {
      if (isNil(groupId)) {
        setOpenMoveSelectedDialog(false);
        return;
      }

      await moveProducts(rowSelectionModel as number[], groupId).then((res) => {
        setOpenMoveSelectedDialog(false);

        return res;
      });
    } catch {
      notifyStore.showNotify('Не удалось переместить товары', 'error');
    }
  };

  const handleMoveIndividualDialogClose = async (groupId?: number): Promise<void> => {
    try {
      if (isNil(actionProductId) || isNil(groupId)) {
        setOpenMoveIndividualDialog(false);
        return;
      }

      await moveProducts([actionProductId], groupId).then((res) => {
        setOpenMoveIndividualDialog(false);

        return res;
      });
    } catch {
      notifyStore.showNotify('Не удалось переместить товары', 'error');
    }
  };

  const handleMoveIndividualProductClick = (productId: number): void => {
    setActionProductId(productId);
    setOpenMoveIndividualDialog(true);
  };

  const moveProducts = async (productIds: number[], groupId?: number): Promise<void> => {
    if (isNil(groupId)) {
      return;
    }

    productIds.forEach(isMoving.add, isMoving);
    setIsMoving(isMoving);

    await productsService.moveProductsToGroupAsync(productIds, groupId).then((res) => {
      productIds.forEach(isMoving.delete, isMoving);
      setIsMoving(isMoving);

      return res;
    });

    await resetProductsData();
  };

  const resetSelection = (): void => {
    setRowSelectionModel([]);
  };

  const handleDeleteSelectedClick = (): void => {
    deleteProduct(rowSelectionModel as number[]);
  };

  const handleGroupsChange = (): void => {
    resetProductsData()
      .then((res) => res)
      .catch((err) => {
        throw err;
      });
  };

  return (
    <SimplePage
      title='Товары'
      header={
        <ProductsPageHeader
          onCreateClick={onCreateClickHandle}
          onSearchChanged={setSearchText}
          searchTextInit={searchText}
        />
      }>
      <ProductDialog
        open={openDetail}
        productId={editedProductId}
        groupId={selectedGroup?.id}
        onClose={handleDetailClose}
      />
      <ProductGroupSelectDialog
        open={openMoveSelectedDialog}
        onClose={(groupId) => {
          handleMoveSelectedDialogClose(groupId)
            .then((res) => res)
            .catch((err) => {
              throw err;
            });
        }}
      />
      <ProductGroupSelectDialog
        open={openMoveIndividualDialog}
        onClose={(groupId) => {
          handleMoveIndividualDialogClose(groupId)
            .then((res) => res)
            .catch((err) => {
              throw err;
            });
        }}
      />

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        gap={2}
        alignItems={{ xs: 'stretch', md: 'start' }}>
        <Stack sx={isLtMdScreen ? {} : { width: 350, minWidth: 350 }} rowGap={2}>
          <Card hidden={rowSelectionModel.length === 0}>
            <CardContent>
              <Stack direction='row' spacing={1} alignItems='center'>
                <div>Выбрано товаров: {rowSelectionModel.length}</div>
                <Button onClick={resetSelection}>Отмена</Button>
              </Stack>
            </CardContent>
            <CardActions>
              <Button
                onClick={() => {
                  setOpenMoveSelectedDialog(true);
                }}>
                Переместить
              </Button>
              <Button color='error' onClick={handleDeleteSelectedClick}>
                Удалить
              </Button>
            </CardActions>
          </Card>

          <Card>
            <CardContent>
              <ProductGroups
                onChange={handleGroupsChange}
                onGroupSelect={(group) => {
                  setSelectedGroup(group);
                }}
                selectedGroup={selectedGroup}
              />
            </CardContent>
          </Card>
        </Stack>
        <Paper sx={{ flex: 'auto' }}>
          <DataGrid
            sx={{
              [`& .${gridClasses.cell}:not(.${gridClasses.cell}Checkbox):not([data-field="actions"])`]:
                {
                  py: 2,
                },
              [`& .${gridClasses.cell}:focus`]: {
                outline: 'none',
              },
              [`& .${gridClasses.row}`]: {
                cursor: 'pointer',
              },
            }}
            rows={data?.items ?? []}
            rowCount={data?.totalCount ?? 0}
            loading={isLoading}
            disableColumnMenu
            autoHeight
            disableRowSelectionOnClick
            onRowClick={rowClickHandle}
            columnVisibilityModel={columnVisibilityModel}
            paginationModel={paginationModel}
            paginationMode='server'
            onPaginationModelChange={setPaginationModel}
            columns={columns}
            getRowHeight={() => 'auto'}
            checkboxSelection
            keepNonExistentRowsSelected
            onRowSelectionModelChange={selectionModelChangeHandler}
            rowSelectionModel={rowSelectionModel}
          />
        </Paper>
      </Stack>
    </SimplePage>
  );
});

export default ProductsPage;
